import { useMutation, useQueryClient } from "@tanstack/react-query";
import {
  emailApi,
  SendEmailRequest,
  SendEmailResponse,
} from "../api/email";

/**
 * Query keys for email-related queries
 * Centralized to ensure consistency across the app
 */
export const EMAIL_QUERY_KEYS = {
  all: ["emails"] as const,
  send: ["emails", "send"] as const,
};

/**
 * Hook to send email with Excel file attachment
 * Uses React Query mutation for state management and error handling
 *
 * @returns Mutation object with mutate function
 *
 * @example
 * ```tsx
 * const sendEmail = useSendEmail();
 *
 * const handleSendEmail = async (file: File) => {
 *   sendEmail.mutate({
 *     excelFile: file,
 *     emailAddress: "client@example.com",
 *     subject: "BOQ Report - Project XYZ",
 *     message: "Please find attached the BOQ report.",
 *     projectName: "Project XYZ"
 *   }, {
 *     onSuccess: (response) => {
 *       notification.success({
 *         message: 'Email Sent',
 *         description: 'The BOQ has been sent successfully!'
 *       });
 *     },
 *     onError: (error) => {
 *       notification.error({
 *         message: 'Email Failed',
 *         description: error.message
 *       });
 *     }
 *   });
 * };
 * ```
 */
export const useSendEmail = () => {
  const queryClient = useQueryClient();

  return useMutation<SendEmailResponse, Error, SendEmailRequest>({
    mutationFn: (data: SendEmailRequest) => emailApi.sendEmail(data),
    onSuccess: (response) => {
      // Invalidate email-related queries if needed
      queryClient.invalidateQueries({
        queryKey: EMAIL_QUERY_KEYS.all,
      });
      console.log("✅ Email sent successfully:", response.message);
    },
    onError: (error) => {
      console.error("❌ Failed to send email:", error.message);
    },
  });
};

/**
 * Type export for external use
 */
export type { SendEmailRequest, SendEmailResponse } from "../api/email";
